import { useCallback, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Hook that provides `get` and `getAll` functions to fetch cached data from React Query
 * @returns {{get: (queryKey: string|Array, options?: {transform?: Function, enabled?: boolean}) => any|null, getAll: (queryKey: string|Array, options?: {transform?: Function, enabled?: boolean}) => any|null}} Object with get and getAll functions
 */
export default function useReactiveQuery() {
  const queryClient = useQueryClient();
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      forceUpdate({});
    });
    return unsubscribe;
  }, [queryClient]);

  const get = useCallback(
    (queryKey, options = {}) => {
      const { transform = (data) => data, enabled = true } = options;

      if (!enabled) {
        return null;
      }

      const cachedData = queryClient.getQueryData(queryKey);
      return cachedData ? transform(cachedData) : null;
    },
    [queryClient]
  );

  const getAll = useCallback(
    (queryKey, options = {}) => {
      const { transform = (data) => data, enabled = true } = options;

      if (!enabled) {
        return null;
      }

      // Get all queries that match the pattern
      const allQueries = queryClient.getQueriesData({ queryKey });
      const allData = allQueries.map(([, data]) => data).filter(Boolean);
      return transform(allData);
    },
    [queryClient]
  );

  return {
    get,
    getAll,
  };
}
